import { IShopifyWindow, Item, ShopifyCart } from "./shopify-types";
import { SimpleCache } from "./simple-cache";
import { IPDPInfo, getPdpInfo } from "./utils";

declare global {
	interface Window {
		Shopify: IShopifyWindow;
	}
}

export enum Env {
	staging = "staging",
	prod = "prod"
}

export interface IConfig {
	env: Env
	merchant_id: string
	publishable_key: string
	checkout_link_host: string
	force_hosted_checkout_redirect: boolean
}

// https://github.com/BoltApp/source/blob/master/hail/bolt/tipser/types/types.go#L13
interface ITCSProduct {
	merchantProductId: string
	variants: ITCSVariant[]
}

// https://github.com/BoltApp/source/blob/master/hail/bolt/tipser/types/types.go#L61
interface ITCSVariant {
	merchantVariantId: string
	stockCount: number
	stockCountIsInfinite: boolean
}

// https://github.com/BoltApp/source/blob/master/hail/bolt/serviceclients/subscriptionsapi/inputs/requests.go#L116
interface ISubscriptionPlans {
	plans: ISubscriptionPlan[]
}

interface ISubscriptionPlan {
	id: string
	name: string
	frequency: number
	frequency_unit: string
}

const TcsConfig = {
	staging: {
		baseUrl: "https://api-staging.boltapp.com",
		posId: "65087023a109749e7cd18b62"
	},
	prod: {
		baseUrl: "https://api.boltapp.com",
		posId: "616839b0a171c90438167d1d"
	}
};

const BoltConfig = {
	staging: {
		baseUrl: 'https://api-staging.boltapp.com',
	},
	prod: {
		baseUrl: 'https://api.boltapp.com',
	}
};

export class BoltButton {
	iframe: HTMLIFrameElement | null = null;
	isMounted: boolean = false;
	subscription_plan_id: string = "";
	quantityCache: SimpleCache<string, number>;
	plansCache: SimpleCache<string, ISubscriptionPlan[]>;
	config: IConfig;

	// Initialized by installContainer
	container!: HTMLDivElement;
	window!: HTMLDivElement;
	windowClose!: HTMLDivElement;

	constructor(config: IConfig) {
		this.iframe = null;
		this.isMounted = false;
		this.subscription_plan_id = "";
		this.config = config;
		this.quantityCache = new SimpleCache();
		this.plansCache = new SimpleCache();
	}

	installContainer() {
		const container = document.getElementById(
			"bolt-express-checkout_container"
		);
		if (container) {
			this.container = <HTMLDivElement>container;
			return;
		}
		const divWithId = (id: string) => {
			const el = document.createElement("div");
			el.id = id;
			return el;
		};
		this.container = divWithId("bolt-express-checkout_container");
		this.container.appendChild(divWithId("bolt-express-checkout_overlay"));

		this.windowClose = divWithId("bolt-express-checkout_window-close");
		this.container.appendChild(this.windowClose);

		this.window = divWithId("bolt-express-checkout_window");
		this.container.appendChild(this.window);

		document.body.appendChild(this.container);

		this.windowClose.addEventListener(
			"click",
			this.handleWindowClose.bind(this)
		);
	}

	async installSubscriptionPlanSelect(select: HTMLSelectElement | null, radioContainer: HTMLUListElement | null) {
		void this.populateSubscriptionPlans(select, radioContainer);
		select?.addEventListener("change", this.handleSubscriptionPlanChange.bind(this));
		const radioItems = radioContainer?.querySelectorAll("input[type=radio][name=subscription]");
		radioItems?.forEach(radio => {
			radio.addEventListener("change", this.handleSubscriptionPlanChange.bind(this));
		});

		const productForm = document.querySelector<HTMLFormElement>("form[data-type=add-to-cart-form]");
		if (productForm === null) {
			throw new Error("Add to cart form not found");
		}
		// variant changed
		productForm.addEventListener("change", this.populateSubscriptionPlans.bind(this, select, radioContainer));
	}

	installCartButton(btn: HTMLElement) {
		btn.addEventListener("click", this.handleCartClick.bind(this));
	}

	installBuyNowButton(btn: HTMLElement) {
		btn.addEventListener("click", this.handlePDPClick.bind(this, false));
	}

	installSubscribeButton(btn: HTMLElement) {
		btn.addEventListener("click", this.handlePDPClick.bind(this, true));
	}

	async populateSubscriptionPlans(select: HTMLSelectElement | null, radioContainer: HTMLUListElement | null) {
		const pdpInfo = getPdpInfo();
		const plans = await this.getAvailablePlans(pdpInfo.productId, pdpInfo.variantId);

		if (select) {
			if (plans.length == 0) {
				select.innerHTML = '<option value="">No plans available</option>';
				return;
			}
			select.innerHTML = '<option value="">Select a plan</option>';
			for (const plan of plans) {
				const option = document.createElement("option");
				option.value = plan.id;
				option.textContent = `${plan.name} - ${plan.frequency} ${plan.frequency_unit}`;
				select.appendChild(option);
			}
		} else if (radioContainer) {
			// first item is always one-time purchase so skipping it here
			const skeletons = [...radioContainer.querySelectorAll(".skeleton-radio-item:not(:first-child)")];
			skeletons.forEach(s => s.remove());

			for (const plan of plans) {
				const li = document.createElement("li");
				li.className = "skeleton-radio-item";

				const label = document.createElement("label");

				const input = document.createElement("input");
				input.type = "radio";
				input.name = "subscription";
				input.value = plan.id;
				input.addEventListener("change", this.handleSubscriptionPlanChange.bind(this));

				label.appendChild(input);
				label.appendChild(document.createTextNode(` ${plan.name} subscription - every ${plan.frequency} ${plan.frequency_unit}(s)`));

				li.appendChild(label);
				radioContainer.appendChild(li);
			}
		}
	}

	async handleSubscriptionPlanChange(event: Event) {
		if (event.target instanceof HTMLSelectElement) {
			this.subscription_plan_id = (<HTMLSelectElement>event.target).value;
		} else if (event.target instanceof HTMLInputElement && event.target.checked) {
			this.subscription_plan_id = (<HTMLInputElement>event.target).value;
		}
		const productFormElem = <HTMLFormElement>document.querySelector("product-form form");
		if (!productFormElem) {
			return;
		}

		await this.populateCartItemSubscriptionProperties(productFormElem);
	}

	async populateCartItemSubscriptionProperties(formElement: HTMLFormElement) {
		formElement.querySelectorAll("input[name^='properties[_bolt_subscription']").forEach(input => input.remove());

		if (!this.subscription_plan_id) {
			return
		}

		const pdpInfo = getPdpInfo();
		const plans = await this.getAvailablePlans(pdpInfo.productId, pdpInfo.variantId);
		const selectedPlan = plans.find(plan => plan.id === this.subscription_plan_id);
		if (!selectedPlan) {
			return;
		}

		const properties: Map<string, string> = new Map([
			// props starting with underscore are not displayed in the cart drawer
			["_bolt_subscription_plan_id", selectedPlan.id],
			["_bolt_subscription_plan_name", selectedPlan.name],
			["_bolt_subscription_plan_frequency", selectedPlan.frequency.toString()],
			["_bolt_subscription_plan_frequency_unit", selectedPlan.frequency_unit],
		]);

		// https://shopify.dev/docs/api/ajax/reference/cart#post--locale-cart-addjs
		properties.forEach((value, key) => {
			const inputElement = document.createElement("input");
			inputElement.type = "hidden";
			inputElement.name = `properties[${key}]`;
			inputElement.value = value;
			formElement.appendChild(inputElement);
		})
	}

	async handlePDPClick(subscription: boolean) {
		if (!this.isMounted) {
			const url = this.getCheckoutLinkBase();

			if (subscription) {
				if (!this.subscription_plan_id) {
					this.showPdpError("You must select a plan first");
					return;
				}
			}

			const pdpInfo = getPdpInfo();
			if (!(await this.validQuantity(pdpInfo))) {
				return;
			}

			url.searchParams.set("merchant_product_id", pdpInfo.productId);
			url.searchParams.set("merchant_variant_id", pdpInfo.variantId);
			url.searchParams.set("quantity", pdpInfo.quantity.toString());
			if (this.subscription_plan_id) {
				url.searchParams.set("subscription_plan_id", this.subscription_plan_id);
			}

			this.openCheckout(url);
		}
	}

	async handleCartClick() {
		if (!this.isMounted) {
			const url = this.getCheckoutLinkBase();

			const cartItems = await this.getCartItems();

			url.searchParams.set("merchant_product_id", cartItems.map(x => x.product_id).join(","));
			url.searchParams.set("merchant_variant_id", cartItems.map(x => x.variant_id).join(","));
			url.searchParams.set("subscription_plan_id", cartItems.map(x => x.properties?.["_bolt_subscription_plan_id"] ?? null).join(","));
			url.searchParams.set("quantity", cartItems.map(x => x.quantity).join(","));

			const discountElem = document.querySelector(".discounts__discount");
			if (discountElem) {
				// use custom bolt elements
				const typeSpan = discountElem.querySelector(".bolt_discount_type");
				const codeSpan = discountElem.querySelector(".bolt_discount_code");
				let discountCode: string = "";

				if (typeSpan && codeSpan) {
					if (typeSpan.textContent === "discount_code") {
						discountCode = codeSpan.textContent?.trim() ?? "";
					}
				} else {
					// space + code + discount info in brackets, example: " Love.com10 (-$6.00)"
					// code can contain spaces and special characters like brackets, so we need to extract it carefully
					const discountText = discountElem.textContent?.trim() ?? "";
					const lastBracketIndex = discountText.lastIndexOf("(");
					discountCode =
						lastBracketIndex > -1
							? discountText.slice(0, lastBracketIndex).trim()
							: discountText;
				}

				if (discountCode && discountCode.length > 0) {
					url.searchParams.set("discount_code", discountCode);
				}
			}

			this.openCheckout(url);
		}
	}

	private async getCartItems(): Promise<Item[]> {
		const response = await fetch(window.Shopify.routes.root + 'cart.js');
		console.log("Bolt Checkout: Fetching cart items from remote cart state");
		const cart: ShopifyCart = await response.json();
		return cart.items;
	}

	getCheckoutLinkBase() {
		const url = new URL("/g", 'https://' + this.config.checkout_link_host);
		url.searchParams.set("publishable_key", this.config.publishable_key);

		return url;
	}

	async validQuantity(pdpInfo: IPDPInfo) {
		if (pdpInfo.quantity < 1) {
			this.showPdpError("Quantity must be greater than 0");
			return false;
		}

		const qty = await this.getQuantityAvailable(pdpInfo.productId, pdpInfo.variantId);

		if (pdpInfo.quantity > qty) {
			if (qty === 1) {
				this.showPdpError("Sorry, there is only 1 item available");
			} else {
				this.showPdpError(`Sorry, there are only ${qty} items available`);
			}
			return false;
		}
		return true;
	}

	openCheckout(url: URL) {
		if (this.config.force_hosted_checkout_redirect) {
			window.location.href = url.href;
		} else {
			this.iframe = this.createBoltExpressIframe(url.href);
			this.mount(this.window, this.iframe);
		}
	}

	showPdpError(message: string) {
		const errorWrapper = document.querySelector(".product-form__error-message-wrapper");
		if (!errorWrapper) {
			console.group("Bolt Checkout Error");
			console.error("Error message wrapper not found");
			console.error("Error message:", message);
			console.groupEnd();
			return;
		}
		errorWrapper.querySelector("span")!.innerText = message;
		errorWrapper.removeAttribute("hidden");
	}

	async getAvailablePlans(productId: string, variantId: string) {
		const cacheKey = `${productId}_${variantId}`;
		return this.plansCache.get(cacheKey, this.fetchPlans(productId, variantId), 3_600_000);
	}

	async fetchPlans(productId: string, variantId: string): Promise<ISubscriptionPlan[]> {
		const plansURL = new URL("/v1/subscriptions/plans", BoltConfig[this.config.env].baseUrl);
		const headers = {
			"x-publishable-key": this.config.publishable_key,
		};

		plansURL.searchParams.set("merchant_product_id", productId);
		plansURL.searchParams.set("merchant_variant_id", variantId);

		const res = await fetch(plansURL, { headers });
		if (!res.ok) {
			console.error("Error loading plans:", res.text());
			return [];
		}
		const plansResp: ISubscriptionPlan[] | ISubscriptionPlans = await res.json();
		if ("plans" in plansResp) {
			return plansResp.plans;
		}
		return plansResp;
	}

	async fetchQuantity(productId: string, variantId: string): Promise<number> {
		const tcs = TcsConfig[this.config.env];
		const url = new URL("/tcs/api/v6/products", tcs.baseUrl);
		url.searchParams.set("merchantId", this.config.merchant_id)
		url.searchParams.set("posId", tcs.posId);
		url.searchParams.set("merchantProductIds", productId);

		const response = await fetch(url);

		if (!response.ok) {
			console.error("Failed to fetch TCS product data");
			return 0;
		}

		const data: ITCSProduct[] = await response.json();

		const product = data.find((p) => p.merchantProductId === productId);
		if (!product) {
			console.error("Product not found in TCS data");
			return 0;
		}

		const variant = product.variants.find((v) => v.merchantVariantId === variantId);
		if (!variant) {
			console.error("Variant not found in TCS data");
			return 0;
		}

		if (variant.stockCountIsInfinite) {
			return Infinity;
		}

		return variant.stockCount;
	}

	async getQuantityAvailable(productId: string, variantId: string) {
		const cacheKey = `${productId}_${variantId}`;
		return this.quantityCache.get(cacheKey, this.fetchQuantity(productId, variantId), 60_000);
	}

	handleWindowClose() {
		if (this.isMounted) {
			this.iframeOnDestroy();
			setTimeout(() => {
				if (this.iframe !== null) {
					this.unmount(this.window, this.iframe);
					this.iframe = null;
				}
			}, 310);
		}
	}

	createBoltExpressIframe(url: string) {
		const iframe = document.createElement("iframe");
		iframe.src = url;
		iframe.id = "bolt-express-checkout_iframe";
		iframe.onload = this.iframeOnLoad.bind(this);
		return iframe;
	}

	iframeOnLoad() {
		this.container.classList.add("visible");
	}

	iframeOnDestroy() {
		this.container.classList.remove("visible");
	}

	mount(parent: HTMLElement, child: HTMLIFrameElement) {
		this.isMounted = true;
		parent.replaceChildren(child);
	}

	unmount(parent: HTMLElement, child: HTMLIFrameElement) {
		parent.removeChild(child);
		this.isMounted = false;
	}
}
